import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Users, Pencil } from "lucide-react";

const EMPTY = { name: "", email: "", phone: "", notes: "", active: true };

export default function Tutors() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const [assign, setAssign] = useState(null);
  const { data: tutors = [] } = useQuery({ queryKey: ["tutors"], queryFn: async () => (await api.get("/modular/tutors")).data });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["tutors"] }); qc.invalidateQueries({ queryKey: ["modular-dashboard"] }); qc.invalidateQueries({ queryKey: ["modular-students"] }); };

  async function save() {
    try {
      const p = { ...edit, email: edit.email || null, phone: edit.phone || null };
      if (edit.id) await api.patch(`/modular/tutors/${edit.id}`, p); else await api.post("/modular/tutors", p);
      setEdit(null); refresh(); toast.success("Tutor salvato");
    } catch (e) { toast.error("Inserisci almeno il nome"); }
  }
  async function del(t) {
    if (!window.confirm(`Eliminare ${t.name}? I suoi studenti resteranno senza tutor.`)) return;
    await api.delete(`/modular/tutors/${t.id}`); refresh();
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1100px] mx-auto" data-testid="tutors-page">
      <PageHeader title="Tutor" subtitle="Tutor di supporto del percorso modulare e studenti assegnati."
        actions={<Button onClick={() => setEdit(EMPTY)} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="tutor-new"><Plus size={14} className="mr-1"/> Nuovo tutor</Button>}/>
      <Card className="border border-stone-200 rounded-xl shadow-none">
        {tutors.length === 0 ? <div className="p-10 text-center text-sm text-stone-400">Nessun tutor. Aggiungi il primo.</div> : (
          <ul className="divide-y divide-stone-100">
            {tutors.map((t) => (
              <li key={t.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`tutor-${t.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium text-[#1C352D]">{t.name} {!t.active && <span className="text-[10px] text-stone-400">(non attivo)</span>}</div>
                  <div className="text-[11px] text-stone-500">{[t.email, t.phone].filter(Boolean).join(" · ") || "Nessun contatto"}</div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setAssign(t)} data-testid={`tutor-assign-${t.id}`}><Users size={13} className="mr-1"/> {t.students_count} studenti</Button>
                <Button variant="ghost" size="icon" onClick={() => setEdit(t)} data-testid={`tutor-edit-${t.id}`}><Pencil size={14}/></Button>
                <Button variant="ghost" size="icon" onClick={() => del(t)} data-testid={`tutor-del-${t.id}`}><Trash2 size={14}/></Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="tutor-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Modifica tutor" : "Nuovo tutor"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Nome e cognome" data-testid="tutor-name"/>
              <Input value={edit.email || ""} onChange={(e) => setEdit({ ...edit, email: e.target.value })} placeholder="Email" data-testid="tutor-email"/>
              <Input value={edit.phone || ""} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} placeholder="Telefono" data-testid="tutor-phone"/>
              <Input value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Note"/>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={edit.active} onCheckedChange={(v) => setEdit({ ...edit, active: !!v })}/> Attivo</label>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="tutor-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <AssignDialog tutor={assign} onClose={() => setAssign(null)} onSaved={refresh}/>
    </div>
  );
}

function AssignDialog({ tutor, onClose, onSaved }) {
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const { data: studs = [] } = useQuery({ queryKey: ["modular-students"], queryFn: async () => (await api.get("/modular/students")).data, enabled: !!tutor });
  const cur = sel ?? new Set(studs.filter((s) => s.tutor_id === tutor?.id).map((s) => s.id));
  const toggle = (id, on) => { const n = new Set(cur); on ? n.add(id) : n.delete(id); setSel(n); };
  const close = () => { setSel(null); setQ(""); onClose(); };
  async function save() {
    await api.put(`/modular/tutors/${tutor.id}/students`, { student_ids: [...cur] });
    toast.success("Assegnazioni salvate"); onSaved(); close();
  }
  const shown = studs.filter((s) => !q || `${s.last_name} ${s.first_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={!!tutor} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-lg bg-white" data-testid="tutor-assign-dialog">
        <DialogHeader><DialogTitle>Studenti di {tutor?.name}</DialogTitle></DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente modulare…" data-testid="tutor-assign-search"/>
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-stone-50 border border-stone-200 rounded-lg">
          {shown.length === 0 && <div className="p-6 text-center text-sm text-stone-400">Nessuno studente modulare.</div>}
          {shown.map((s) => (
            <label key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer" data-testid={`tutor-stud-${s.id}`}>
              <Checkbox checked={cur.has(s.id)} onCheckedChange={(v) => toggle(s.id, !!v)}/>
              <span className="flex-1">{s.last_name} {s.first_name}</span>
              <span className="text-[10px] text-stone-400">{s.year_level ? `${s.year_level}° anno` : ""}{s.tutor_id && s.tutor_id !== tutor?.id ? " · ha altro tutor" : ""}</span>
            </label>
          ))}
        </div>
        <DialogFooter><Button onClick={save} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="tutor-assign-save">Salva ({cur.size})</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
